import { nanoid } from 'nanoid';
import { ReactComponent as PaidLeaveIcon } from '../assets/icons/event_busy.svg';
import { ReactComponent as SickDayIcon } from '../assets/icons/local_hospital.svg';
import { ReactComponent as SickLeaveIcon } from '../assets/icons/local_hotel.svg';
import { ReactComponent as ParentalLeaveIcon } from '../assets/icons/escalator_warning.svg';
import { ReactComponent as PersonalDayIcon } from '../assets/icons/person.svg';
import { ReactComponent as UnpaidLeaveIcon } from '../assets/icons/money_off.svg';
import { ReactComponent as HolidayIcon } from '../assets/icons/celebration.svg';
import { primaryPalette } from '../styles/theme';
import { SINGLE_DAY_SAVE_FORMAT } from './date';
import { FIELD_TYPES } from './fieldTypes';
import {
  getStartOfYear,
  getEndOfYear,
  formatDate,
  addYearsToDate,
  disableLeapDay,
} from '../utility/dateUtils';
import {
  isEmpty,
  validateLength,
  forbiddenCharacters,
} from '../utility/validation';
import { isArrayEmpty } from '../utility/helpers';

const ABSENCE_ROLL_OVER_DATE_FORMAT = 'MMM d';

export const ABSENCE_DEFAULT_REASONS = ['Reason'];

export const ABSENCE_COLORS = [
  primaryPalette.yellow5,
  primaryPalette.red5,
  primaryPalette.blue10,
  primaryPalette.blue9,
  primaryPalette.red7,
  primaryPalette.red6,
];

export const DEFAULT_NUMBER_OF_DAYS_COUNT = 20;

export const ACCRUAL_OPTION_KEYS = {
  YEARLY: 'YEARLY',
  MONTHLY: 'MONTHLY',
};

export const ACCRUAL_OPTIONS = [
  {
    value: ACCRUAL_OPTION_KEYS.YEARLY,
    name: 'Yearly',
    key: 'yearly',
  },
  {
    value: ACCRUAL_OPTION_KEYS.MONTHLY,
    name: 'Monthly',
    key: 'monthly',
  },
];

export const ABSENCE_ICONS = [
  {
    id: 1,
    translationKey: 'vacationLeave',
    icon: PaidLeaveIcon,
    label: 'Vacation leave',
  },
  {
    id: 2,
    translationKey: 'sickDay',
    icon: SickDayIcon,
    label: 'Sick day',
  },
  {
    id: 3,
    translationKey: 'personalDays',
    icon: PersonalDayIcon,
    label: 'Personal days',
  },
  {
    id: 4,
    translationKey: 'parentalLeave',
    icon: ParentalLeaveIcon,
    label: 'Parental leave',
  },
  {
    id: 5,
    translationKey: 'unpaidLeave',
    icon: UnpaidLeaveIcon,
    label: 'Unpaid leave',
  },
  {
    id: 6,
    translationKey: 'sickLeave',
    icon: SickLeaveIcon,
    label: 'Sick leave',
  },
];

export const ABSENCE_SET_FIELD_NAMES = {
  NAME: 'name',
  TYPES: 'types',
};

export const ABSENCE_FIELD_NAMES = {
  NAME: 'name',
  ORDER: 'order',
  COLOR: 'color',
  ICON: 'icon',
  TOTAL: 'total',
  DAYS: 'days',
  HAS_DAYS_ENABLED: 'days_enabled',
  NUMBER_OF_DAYS: 'number_of_days',
  SCHEDULED_DAYS: 'number_of_scheduled_days',
  ACCRUAL: 'accrual',
  HAS_ROLLOVER: 'rollover_enabled',
  ROLLOVER_DATE: 'rollover_date',
  HAS_EXTENSIONS: 'extensions_enabled',
  EXTENSIONS: 'absence_extensions',
  HAS_TEMPLATE: 'pdf_report_required',
  HAS_REASONS: 'reason_enabled',
  REASONS: 'reasons',
  CAN_EDIT_PREVIOUS_YEAR_BALANCE: 'can_edit_balance_previous_year',
  USED_DAYS: 'used_days',
  AVAILABLE_DAYS: 'available_days',
  USER_COUNT: 'user_count',
  ABSENCE_SET_ID: 'absence_set',
  IS_PREVIOUS_YEAR_MANUAL_BALANCE: 'is_manual_total',
  TOTAL_PREVIOUS_YEAR: 'total_previous_year',
  CURRENT_YEAR_ADJUSTMENT_DAYS: 'adjustment_days',
  DESCRIPTION: 'description',
  TEMPLATE: 'template',
  EXPIRATION_DATE: 'expiration_date',
  CARRY_OVER: 'carry_over',
  ADJUSTMENTS: 'adjustments',
};

export const ABSENCE_REQUEST_FIELD_NAMES = {
  ABSENCE_SET: 'absence_set',
  ABSENCE: 'absence',
  AMOUNT: 'amount',
  ABSENCE_FROM: 'absence_from',
  ABSENCE_TO: 'absence_to',
  DESCRIPTION: 'description',
  USER: 'user',
  USED_DAYS: 'used_days',
  YEAR: 'year',
  REASON: 'reason',
};

export const ABSENCE_FIELDS = [
  {
    name: ABSENCE_FIELD_NAMES.NAME,
    type: FIELD_TYPES.TEXT,
    fieldWrapperClass: 'title',
    required: true,
    validators: [
      {
        validator: isEmpty,
        type: 'required',
      },
      {
        validator: forbiddenCharacters,
        type: 'forbiddenCharacters',
      },
      {
        validator: fieldValue => validateLength(fieldValue, 0, 255),
        type: 'nameMaxLength',
      },
    ],
  },
  {
    name: ABSENCE_FIELD_NAMES.ICON,
    type: FIELD_TYPES.SELECT,
    optionsKey: 'absenceIcons',
    parser: {
      label: 'icon',
      value: 'id',
    },
    isIcon: true,
    isSearchDisabled: true,
  },
  {
    name: ABSENCE_FIELD_NAMES.COLOR,
    type: FIELD_TYPES.COLOR_PICKER,
    collorPalette: ABSENCE_COLORS,
    shouldDisableCustomColorPick: true,
  },
  {
    name: ABSENCE_FIELD_NAMES.HAS_DAYS_ENABLED,
    type: FIELD_TYPES.SWITCH,
    translationKey: 'hasAvailableDays',
    isHighlightedLabel: true,
    isDisabledCheck: values =>
      values[ABSENCE_FIELD_NAMES.HAS_DAYS_ENABLED] &&
      values[ABSENCE_FIELD_NAMES.HAS_EXTENSIONS] &&
      !isArrayEmpty(values[ABSENCE_FIELD_NAMES.EXTENSIONS]) &&
      values[ABSENCE_FIELD_NAMES.EXTENSIONS].some(
        extension => extension[ABSENCE_FIELD_NAMES.USER_COUNT] > 0
      ),
    dependants: [
      {
        name: ABSENCE_FIELD_NAMES.NUMBER_OF_DAYS,
        resetFnc: async (
          prevHasDaysEnabled,
          currentHasDaysEnabled,
          currentNumberOfDays,
          initialValues
        ) => {
          if (currentHasDaysEnabled) {
            return (
              initialValues[ABSENCE_FIELD_NAMES.NUMBER_OF_DAYS] ||
              DEFAULT_NUMBER_OF_DAYS_COUNT
            );
          }

          return 0;
        },
      },
      {
        name: ABSENCE_FIELD_NAMES.ACCRUAL,
        resetFnc: async (
          prevHasDaysEnabled,
          currentHasDaysEnabled,
          currentAccrual,
          initialValues
        ) => {
          if (currentHasDaysEnabled) {
            return (
              initialValues[ABSENCE_FIELD_NAMES.ACCRUAL] ||
              ACCRUAL_OPTION_KEYS.YEARLY
            );
          }

          return null;
        },
      },
      {
        name: ABSENCE_FIELD_NAMES.HAS_ROLLOVER,
        resetFnc: async (
          prevHasDaysEnabled,
          currentHasDaysEnabled,
          currentHasRollover,
          initialValues
        ) => {
          if (currentHasDaysEnabled) {
            return initialValues[ABSENCE_FIELD_NAMES.HAS_ROLLOVER] || false;
          }

          return false;
        },
      },
      {
        name: ABSENCE_FIELD_NAMES.ROLLOVER_DATE,
        resetFnc: async (
          prevHasDaysEnabled,
          currentHasDaysEnabled,
          currentRolloverDate,
          initialValues
        ) => {
          if (currentHasDaysEnabled) {
            return (
              initialValues[ABSENCE_FIELD_NAMES.ROLLOVER_DATE] ||
              formatDate(addYearsToDate(), SINGLE_DAY_SAVE_FORMAT)
            );
          }

          return null;
        },
      },
      {
        name: ABSENCE_FIELD_NAMES.HAS_EXTENSIONS,
        resetFnc: async (
          prevHasDaysEnabled,
          currentHasDaysEnabled,
          currentHasExtensions,
          initialValues
        ) => {
          if (currentHasDaysEnabled) {
            return initialValues[ABSENCE_FIELD_NAMES.HAS_EXTENSIONS] || false;
          }

          return false;
        },
      },
    ],
  },
  {
    name: ABSENCE_FIELD_NAMES.NUMBER_OF_DAYS,
    type: FIELD_TYPES.TEXT,
    inputType: 'number',
    isToggleable: true,
    shouldRender: values => values[ABSENCE_FIELD_NAMES.HAS_DAYS_ENABLED],
    inputProps: {
      min: 1,
      max: 365,
    },
    required: true,
    validators: [
      { type: 'required', validator: isEmpty },
      {
        type: 'minMaxDays',
        shouldPassFormValues: true,
        validator: (value, values) =>
          values[ABSENCE_FIELD_NAMES.HAS_DAYS_ENABLED] ? value >= 1 : true,
      },
    ],
  },
  {
    name: ABSENCE_FIELD_NAMES.ACCRUAL,
    type: FIELD_TYPES.SELECT,
    translationKey: 'accrual',
    required: true,
    optionsKey: 'accrualOptions',
    isSearchDisabled: true,
    isToggleable: true,
    shouldRender: values => values[ABSENCE_FIELD_NAMES.HAS_DAYS_ENABLED],
    parser: {
      value: 'value',
      label: 'name',
    },
  },
  {
    name: ABSENCE_FIELD_NAMES.HAS_ROLLOVER,
    type: FIELD_TYPES.SWITCH,
    translationKey: 'hasRollover',
    isHighlightedLabel: true,
    isToggleable: true,
    shouldRender: values => values[ABSENCE_FIELD_NAMES.HAS_DAYS_ENABLED],
    dependants: [
      {
        name: ABSENCE_FIELD_NAMES.ROLLOVER_DATE,
        resetFnc: async (
          prevHasRollover,
          currentHasRollover,
          currentRolloverDate,
          initialValues
        ) => {
          if (currentHasRollover) {
            return (
              initialValues[ABSENCE_FIELD_NAMES.ROLLOVER_DATE] ||
              formatDate(addYearsToDate(), SINGLE_DAY_SAVE_FORMAT)
            );
          }

          return null;
        },
      },
    ],
  },
  {
    name: ABSENCE_FIELD_NAMES.ROLLOVER_DATE,
    type: FIELD_TYPES.DATE_PICKER,
    translationKey: 'rolloverDate',
    valueFormat: SINGLE_DAY_SAVE_FORMAT,
    selectedDateFormat: ABSENCE_ROLL_OVER_DATE_FORMAT,
    isToggleable: true,
    shouldDisableDate: disableLeapDay,
    getMinDate: () => getStartOfYear(addYearsToDate()),
    getMaxDate: () => getEndOfYear(addYearsToDate()),
    shouldRender: values =>
      values[ABSENCE_FIELD_NAMES.HAS_DAYS_ENABLED] &&
      values[ABSENCE_FIELD_NAMES.HAS_ROLLOVER],
  },
  {
    name: ABSENCE_FIELD_NAMES.HAS_EXTENSIONS,
    translationKey: 'hasExtensions',
    type: FIELD_TYPES.CHECKBOX,
    isControlled: true,
    smallText: true,
    isToggleable: true,
    isDisabledCheck: values =>
      values[ABSENCE_FIELD_NAMES.HAS_DAYS_ENABLED] &&
      values[ABSENCE_FIELD_NAMES.HAS_EXTENSIONS] &&
      !isArrayEmpty(values[ABSENCE_FIELD_NAMES.EXTENSIONS]) &&
      values[ABSENCE_FIELD_NAMES.EXTENSIONS].some(
        extension => extension[ABSENCE_FIELD_NAMES.USER_COUNT] > 0
      ),
    shouldRender: values => values[ABSENCE_FIELD_NAMES.HAS_DAYS_ENABLED],
  },
  {
    name: ABSENCE_FIELD_NAMES.HAS_TEMPLATE,
    translationKey: 'hasTemplate',
    type: FIELD_TYPES.CHECKBOX,
    isControlled: true,
    smallText: true,
  },
  {
    name: ABSENCE_FIELD_NAMES.HAS_REASONS,
    translationKey: 'hasReasons',
    type: FIELD_TYPES.CHECKBOX,
    isControlled: true,
    smallText: true,
    dependants: [
      {
        name: ABSENCE_FIELD_NAMES.REASONS,
        resetFnc: async (
          prevHasReasonsEnabled,
          currentHasReasonsEnabled,
          currentReasons,
          initialValues
        ) => {
          if (currentHasReasonsEnabled) {
            return !isArrayEmpty(initialValues[ABSENCE_FIELD_NAMES.REASONS])
              ? initialValues[ABSENCE_FIELD_NAMES.REASONS]
              : [{ id: nanoid(), name: ABSENCE_DEFAULT_REASONS[0] }];
          }

          return [];
        },
      },
    ],
  },
];

export const ABSENCE_REASON_FIELD = {
  name: 'name',
  required: true,
  validators: [
    { type: 'required', validator: isEmpty },
    {
      type: 'nameMaxLength',
      validator: value => validateLength(value, 0, 255),
    },
    {
      type: 'forbiddenCharacters',
      validator: forbiddenCharacters,
    },
  ],
};

export const HOLIDAY = {
  id: 'HOLIDAY',
  absenceIcon: HolidayIcon,
  name: 'Holiday',
  key: 'holiday',
};

export const INITIAL_ABSENCE_REQUESTS_SORT = {
  column: 'createdAt',
  asc: false,
  sortKey: '-created_at',
};

export const ABSENCE_CALENDAR_INTERVAL_TYPES = {
  WEEK: 'WEEK',
  MONTH: 'MONTH',
};

export const ABSENCE_CALENDAR_INTERVALS = [
  {
    value: ABSENCE_CALENDAR_INTERVAL_TYPES.WEEK,
    label: 'Week',
    key: 'week',
  },
  {
    value: ABSENCE_CALENDAR_INTERVAL_TYPES.MONTH,
    label: 'Month',
    key: 'month',
  },
];

export const ABSENCE_REPORT_BALANCE_KEYS = {
  AVAILABLE: 'total_available_days',
  ACCRUED: 'total_accrued_days',
  TAKEN: 'total_spent_days_this_year',
  SCHEDULED: 'total_scheduled_days_this_year',
};

export const ABSENCE_REPORT_BALANCE_ITEMS = [
  { key: 'available', value: ABSENCE_REPORT_BALANCE_KEYS.AVAILABLE },
  { key: 'accrued', value: ABSENCE_REPORT_BALANCE_KEYS.ACCRUED },
  { key: 'taken', value: ABSENCE_REPORT_BALANCE_KEYS.TAKEN },
  { key: 'scheduled', value: ABSENCE_REPORT_BALANCE_KEYS.SCHEDULED },
];
